import { Combobox, TextInput, useCombobox } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import axios, { AxiosResponse } from 'axios';
import { useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { AutoCompleteResult } from '@/models/AddressComplete.ts';
import { EditPatientFormData, PatientFormData } from '@/models/Patient.ts';

import { MINIMUM_SEARCH_LENGTH, buildDisplayName, buildResults, handleSearch } from '@/utils/addressUtils.ts';

interface Props {
    form: UseFormReturnType<PatientFormData | EditPatientFormData>;
    onSelect: (value: AutoCompleteResult) => void;
    withAsterisk?: boolean;
}

export const AddressAutocomplete = ({ form, onSelect, withAsterisk = false }: Props) => {
    const [query, setQuery] = useState<string>('');
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const fetcher = async (): Promise<AutoCompleteResult[]> => {
        const response: AxiosResponse<AutoCompleteResult[]> = await axios.get(
            'https://api.locationiq.com/v1/autocomplete',
            {
                params: {
                    key: import.meta.env.VITE_LOCATIONIQ_TOKEN,
                    q: query,
                    countrycodes: 'de',
                    dedupe: '1',
                    ['accept-language']: 'de',
                },
            }
        );

        if (!response.data || response.data.length === 0) return [];

        const results: AutoCompleteResult[] = buildResults(response.data);

        return results.map((result: AutoCompleteResult) => ({
            ...result,
            display_name: buildDisplayName(result),
        }));
    };

    const { data: results } = useSWRImmutable(
        query.length >= MINIMUM_SEARCH_LENGTH ? `address-autocomplete-${query.toLowerCase().trim()}` : undefined,
        fetcher
    );

    const handleOptionSubmit = (value: string): void => {
        const selected_result = results?.find((result) => result.osm_id === value);

        if (!selected_result) return;

        onSelect(selected_result);
        combobox.closeDropdown();
    };

    const shouldShowDropdown = (value: string): boolean => {
        return value.length >= MINIMUM_SEARCH_LENGTH && results !== undefined && results.length > 0;
    };

    const options = useMemo(
        () =>
            results?.map((result) => (
                <Combobox.Option
                    value={result.osm_id}
                    key={result.osm_id}
                    className="p-3 text-sm transition hover:bg-slate-50"
                >
                    {result.display_name}
                </Combobox.Option>
            )) ?? [],
        [results]
    );

    const handleFocus = (): void => {
        const current_value = form.values.street || '';
        if (shouldShowDropdown(current_value)) {
            combobox.openDropdown();
        }
    };

    return (
        <Combobox store={combobox} withinPortal={false} onOptionSubmit={handleOptionSubmit}>
            <Combobox.Target>
                <TextInput
                    label="Adresse" // TODO: translation
                    placeholder="Adresse" // TODO: translation
                    withAsterisk={withAsterisk}
                    value={form.values.street ?? ''}
                    onChange={(event) => {
                        const value = event.currentTarget.value;
                        form.setFieldValue('street', value);
                        handleSearch(value, setQuery, combobox);
                    }}
                    onFocus={handleFocus}
                    onBlur={() => combobox.closeDropdown()}
                />
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options className="max-h-52 overflow-y-auto">
                    {options.length > 0
                        ? options
                        : query.length >= MINIMUM_SEARCH_LENGTH && (
                              <Combobox.Empty className="p-3 text-sm text-slate-400">
                                  Keine Ergebnisse gefunden
                              </Combobox.Empty>
                          )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
